import React from 'react';
import {
  Box,
  Paper,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { lt } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { useStiklainisCalendar } from './stiklainis/useStiklainisCalendar';
import StiklainisCalendarToolbar from './stiklainis/StiklainisCalendarToolbar';
import StiklainisWeekView from './stiklainis/StiklainisWeekView';
import StiklainisMonthView from './stiklainis/StiklainisMonthView';
import ReservationFormDialog from './stiklainis/ReservationFormDialog';
import ReservationInfoDialog from './stiklainis/ReservationInfoDialog';
import CalendarExportMenu from './stiklainis/CalendarExportMenu';
import { ConferenceRoom } from './stiklainis/types';

interface SimpleCalendarViewProps {
  selectedRoom?: string;
  onRoomChange?: (roomId: string) => void;
  onRoomsLoaded?: (rooms: ConferenceRoom[]) => void;
}

const SimpleCalendarView: React.FC<SimpleCalendarViewProps> = ({
  selectedRoom,
  onRoomChange,
  onRoomsLoaded,
}) => {
  const { t } = useTranslation();
  const calendar = useStiklainisCalendar({ selectedRoom, onRoomChange, onRoomsLoaded });

  const showInitialLoader = calendar.loading && calendar.rooms.length === 0;

  if (showInitialLoader) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={lt}>
      <Box>
        {calendar.error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => calendar.setError(null)}>
            {calendar.error}
          </Alert>
        )}

        <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
          <StiklainisCalendarToolbar
            dateRangeLabel={calendar.dateRangeLabel}
            viewMode={calendar.viewMode}
            rooms={calendar.rooms}
            selectedRoomId={calendar.effectiveRoomId}
            onRoomChange={onRoomChange}
            onPrevious={calendar.handlePrevious}
            onNext={calendar.handleNext}
            onToday={calendar.handleToday}
            onViewModeChange={calendar.setViewMode}
            onCreateReservation={calendar.handleCreateReservation}
          />

          <Box sx={{ p: { xs: 1, sm: 2 }, maxHeight: 'calc(100vh - 280px)', overflowY: 'auto' }}>
            {calendar.viewMode === 'week' ? (
              <StiklainisWeekView
                currentDate={calendar.currentDate}
                reservations={calendar.reservations}
                selectedRoomId={calendar.effectiveRoomId}
                currentUserId={calendar.user?.id}
                dayNames={calendar.dayNames}
                onDayClick={calendar.handleDayClick}
                onReservationClick={calendar.handleReservationClick}
              />
            ) : (
              <StiklainisMonthView
                currentDate={calendar.currentDate}
                reservations={calendar.reservations}
                selectedRoomId={calendar.effectiveRoomId}
                currentUserId={calendar.user?.id}
                dayNames={calendar.dayNames}
                selectedMobileDay={calendar.selectedMobileDay}
                onSelectedMobileDayChange={calendar.setSelectedMobileDay}
                dayAgendaDate={calendar.dayAgendaDate}
                onOpenDayAgenda={calendar.openDayAgenda}
                onCloseDayAgenda={calendar.closeDayAgenda}
                onDayCreate={(day) => calendar.handleTimeSlotClick(day, '09:00')}
                onReservationClick={calendar.handleReservationClick}
              />
            )}
          </Box>
        </Paper>

        <ReservationFormDialog
          open={calendar.openDialog}
          editingReservation={calendar.editingReservation}
          formData={calendar.formData}
          rooms={calendar.rooms}
          notice={calendar.reservationDialogNotice}
          onClose={() => {
            calendar.setOpenDialog(false);
            calendar.setReservationDialogNotice(null);
          }}
          onSave={calendar.handleSaveReservation}
          onCancelReservation={
            calendar.editingReservation
              ? () =>
                  calendar.requestCancelReservation(
                    calendar.editingReservation!.id,
                    calendar.editingReservation
                  )
              : undefined
          }
          onFormChange={calendar.handleFormChange}
          onClearNotice={() => calendar.setReservationDialogNotice(null)}
          canDelete={calendar.editingReservation ? calendar.canDeleteReservation(calendar.editingReservation) : false}
        />

        <ReservationInfoDialog
          open={calendar.infoDialogOpen}
          reservation={calendar.infoReservation}
          canDelete={calendar.infoReservation ? calendar.canDeleteReservation(calendar.infoReservation) : false}
          onClose={() => calendar.setInfoDialogOpen(false)}
          onDelete={async () => {
            if (calendar.infoReservation) {
              calendar.requestCancelReservation(
                calendar.infoReservation.id,
                calendar.infoReservation
              );
            }
          }}
          onOpenCalendarMenu={(e) => {
            if (calendar.infoReservation) {
              calendar.setCalendarMenuAnchor(e.currentTarget);
              calendar.setCalendarMenuReservation(calendar.infoReservation);
            }
          }}
        />

        <CalendarExportMenu
          anchorEl={calendar.calendarMenuAnchor}
          reservation={calendar.calendarMenuReservation}
          onClose={() => {
            calendar.setCalendarMenuAnchor(null);
            calendar.setCalendarMenuReservation(null);
          }}
        />

        <Dialog
          open={calendar.deleteSeriesPrompt.open}
          onClose={() => calendar.setDeleteSeriesPrompt({ open: false, reservationId: null })}
        >
          <DialogTitle>{t('stiklainis.deleteSeriesTitle', 'Ištrinti seriją?')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t(
                'stiklainis.deleteSeriesBody',
                'Šis susitikimas priklauso kartojamai serijai. Ištrinti tik šį, ar visą seriją?'
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 2, pb: 2 }}>
            <Button
              onClick={() => calendar.setDeleteSeriesPrompt({ open: false, reservationId: null })}
            >
              {t('common.cancel')}
            </Button>
            <Button
              color="error"
              variant="outlined"
              onClick={() => {
                if (calendar.deleteSeriesPrompt.reservationId) {
                  void calendar.performCancelReservation(
                    calendar.deleteSeriesPrompt.reservationId,
                    false
                  );
                }
              }}
            >
              {t('stiklainis.deleteThisOnly', 'Tik šį')}
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={() => {
                if (calendar.deleteSeriesPrompt.reservationId) {
                  void calendar.performCancelReservation(
                    calendar.deleteSeriesPrompt.reservationId,
                    true
                  );
                }
              }}
            >
              {t('stiklainis.deleteEntireSeries', 'Visą seriją')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default SimpleCalendarView;
